export const AGENT_TASK_NOTIFICATION_PROMPT =
  "Get notified when agents complete tasks. Enable Notifications?";

const ENABLED_KEY = "fintheon:agent-task-notifications:enabled";
const LATER_UNTIL_KEY = "fintheon:agent-task-notifications:later-until";
const LATER_MS = 24 * 60 * 60 * 1000;
const CHAT_SURFACE_SELECTOR = "[data-agent-chat-surface]";
const TERMINAL_STATUSES = new Set(["completed", "failed", "cancelled"]);

export interface AgentTaskNotificationLike {
  title?: string;
  category?: string;
  eventId?: string;
  metadata?: Record<string, unknown>;
}

export function isAgentTaskNotification(
  notification: AgentTaskNotificationLike,
): boolean {
  const metadata = recordValue(notification.metadata);
  const status = stringValue(metadata?.status);
  const hasAgenticMetadata =
    Boolean(stringValue(metadata?.taskId)) ||
    Boolean(stringValue(metadata?.requestId)) ||
    Boolean(stringValue(metadata?.kind)) ||
    Boolean(stringValue(metadata?.mode));

  if (hasAgenticMetadata && (!status || TERMINAL_STATUSES.has(status))) {
    return true;
  }

  const title = notification.title ?? "";
  return /^(Agent|NarrativeFlow|Desk|Scout)\s+(task|run)\s+(finished|failed|cancelled|done)/i.test(
    title,
  );
}

export function agentTaskNotificationEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(ENABLED_KEY) === "true";
  } catch {
    return false;
  }
}

export function markAgentTaskNotificationsEnabled() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ENABLED_KEY, "true");
    window.localStorage.removeItem(LATER_UNTIL_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted contexts.
  }
}

export function snoozeAgentTaskNotificationPrompt(now = Date.now()) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LATER_UNTIL_KEY, String(now + LATER_MS));
  } catch {
    // Storage can be unavailable in privacy-restricted contexts.
  }
}

export function shouldShowAgentTaskNotificationPrompt(now = Date.now()) {
  if (agentTaskNotificationEnabled()) return false;
  if (typeof window === "undefined") return false;
  try {
    const laterUntil = Number(window.localStorage.getItem(LATER_UNTIL_KEY));
    return !Number.isFinite(laterUntil) || laterUntil <= now;
  } catch {
    return true;
  }
}

export function isAppOutOfView(): boolean {
  if (typeof document === "undefined") return false;
  return document.visibilityState !== "visible" || !document.hasFocus();
}

export function hasVisibleAgentChatSurface(): boolean {
  if (typeof document === "undefined") return false;
  const nodes = Array.from(document.querySelectorAll(CHAT_SURFACE_SELECTOR));
  return nodes.some(isVisibleElement);
}

function isVisibleElement(node: Element): boolean {
  const element = node as HTMLElement;
  const rect = element.getBoundingClientRect();
  if (rect.width < 8 || rect.height < 8) return false;

  if (typeof window === "undefined" || !window.getComputedStyle) return true;
  const style = window.getComputedStyle(element);
  return (
    style.display !== "none" &&
    style.visibility !== "hidden" &&
    Number(style.opacity || "1") > 0
  );
}

function recordValue(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}
